import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Input } from '@/components/ui/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Loader2, Send } from 'lucide-react'
import { toast } from 'sonner'
import type { RolNivel } from '@/types'

interface Props {
  orgId: string
  // When provided, routes invitations through invite_tour_member so the
  // invitee also lands in this tour's membership (or carries this tour in
  // tour_ids if the email isn't a real user yet).
  tourId?: string
  // Keys that refer to the members and invitations lists so we can
  // invalidate them from a single place after a successful invite.
  invalidationKeys?: string[][]
}

// Invokes invite_member (org) or invite_tour_member (tour) RPC: if the email
// already belongs to a real user, the server creates the membership directly;
// otherwise it stores a pending invitation that gets accepted on their next
// login.
export function InviteMemberForm({ orgId, tourId, invalidationKeys = [] }: Props) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [rol, setRol] = useState<RolNivel>('C')

  const invite = useMutation({
    mutationFn: async () => {
      if (tourId) {
        const { data, error } = await supabase.rpc('invite_tour_member', {
          target_tour_id: tourId,
          target_email: email.trim(),
          target_rol_nivel: rol,
        })
        if (error) throw error
        return data as { status: 'added' | 'invited'; email?: string }
      }
      const { data, error } = await supabase.rpc('invite_member', {
        target_org_id: orgId,
        target_email: email.trim(),
        target_rol_nivel: rol,
      })
      if (error) throw error
      return data as { status: 'added' | 'invited'; email?: string }
    },
    onSuccess: (data) => {
      if (data.status === 'added') toast.success('Member added')
      else toast.success("Invitation saved — they'll join on their next login")
      setEmail('')
      for (const key of invalidationKeys) {
        queryClient.invalidateQueries({ queryKey: key })
      }
    },
    onError: (err: Error) => toast.error(err.message),
  })

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        if (!email.trim()) return
        invite.mutate()
      }}
      className="space-y-2"
    >
      <Input
        type="email"
        placeholder="email@example.com"
        value={email}
        onChange={e => setEmail(e.target.value)}
        className="h-11 text-sm"
        autoComplete="email"
      />
      <div className="grid grid-cols-[auto_1fr] gap-2">
        <Select value={rol} onValueChange={v => setRol(v as RolNivel)}>
          <SelectTrigger className="w-32 !h-11 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="min-w-40 p-1.5">
            <SelectItem value="A" className="py-2.5 text-sm">Manager</SelectItem>
            <SelectItem value="B" className="py-2.5 text-sm">Artist</SelectItem>
            <SelectItem value="C" className="py-2.5 text-sm">Crew</SelectItem>
          </SelectContent>
        </Select>
        <button
          type="submit"
          disabled={invite.isPending || !email.trim()}
          className="login-btn compact accent gap-2"
          style={{ boxShadow: 'none' }}
        >
          {invite.isPending
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Send className="w-4 h-4" />}
          {invite.isPending ? 'Sending...' : 'Invite'}
        </button>
      </div>
    </form>
  )
}
